import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { FiMenu, FiX, FiArrowRight } from 'react-icons/fi';

export function MainLayout() {
  const { user, profile, loading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/' + targetId);
    } else {
      const element = document.getElementById(targetId.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How it Works', href: '#how-it-works' },
  ];

  const dashboardPath = profile?.role === 'admin' 
    ? '/dashboard/admin' 
    : profile?.role === 'driver' 
      ? '/dashboard/driver' 
      : '/dashboard/business';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <Logo />

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-bold text-slate-600 hover:text-primary-500 transition-colors uppercase tracking-wider"
              >
                {link.label}
              </a>
            ))}
            <div className="h-4 w-px bg-slate-200" />
            {user && !loading ? (
              <Link to={dashboardPath}>
                <Button size="sm" className="rounded-full px-6">Dashboard</Button>
              </Link>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-sm font-bold text-slate-600 hover:text-primary-500">
                  Log in
                </Link>
                <Link to="/register">
                  <Button size="sm" className="rounded-full px-6">Get Started</Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-200 shadow-xl overflow-hidden z-40"
            >
              <div className="p-6 flex flex-col gap-6">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center justify-between"
                  >
                    {link.label}
                    <FiArrowRight className="text-primary-500" />
                  </a>
                ))}
                <div className="h-px bg-slate-100" />
                {user ? (
                  <Link to={dashboardPath} onClick={() => setIsMenuOpen(false)}>
                    <Button className="w-full h-12 rounded-2xl">Go to Dashboard</Button>
                  </Link>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full h-12 rounded-2xl">Log in</Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full h-12 rounded-2xl">Sign up Free</Button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <Logo className="justify-center mb-6 grayscale opacity-50" showText={false} />
          <p className="font-bold uppercase tracking-widest text-[10px]">
            © {new Date().getFullYear()} Roaming Logos. Island-wide Advertising.
          </p>
        </div>
      </footer>
    </div>
  );
}